import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'
import { avatarColor, avatarInitial } from '../lib/avatar'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface PersonRow {
  key: string
  label: string
  email?: string
  meetingCount: number
  lastSeen: string | null
  photoURL?: string
  isMember: boolean
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
}

/**
 * ユーザーリスト: 議事録に登場した人物を新しい順に表示
 */
export function PeoplePage() {
  const { firebaseUser } = useAuth()
  const [people, setPeople] = useState<PersonRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [mergeTarget, setMergeTarget] = useState<string>('')
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [merging, setMerging] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [addName, setAddName] = useState('')
  const [addEmail, setAddEmail] = useState('')
  const [adding, setAdding] = useState(false)

  const handleAddPerson = async () => {
    if (!firebaseUser) return
    setAdding(true)
    setError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/people`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: addName.trim(), email: addEmail.trim() })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `人物の登録に失敗しました (${res.status})`)
      setAddName('')
      setAddEmail('')
      setShowAdd(false)
      await fetchPeople()
    } catch (e) {
      setError(e instanceof Error ? e.message : '人物の登録に失敗しました')
    } finally {
      setAdding(false)
    }
  }

  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const fetchPeople = useCallback(async () => {
    if (!firebaseUser) return
    setLoading(true)
    setError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/people/list`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error(`人物リストの取得に失敗しました (${res.status})`)
      const data = await res.json()
      setPeople(data.people || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : '人物リストの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [firebaseUser])

  useEffect(() => { fetchPeople() }, [fetchPeople])

  // 選択した人物を1人（mergeTarget）に統合する
  const handleBulkMerge = async () => {
    if (!firebaseUser || !mergeTarget || selected.size < 2) return
    setMerging(true)
    setError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const targetPerson = people.find((p) => p.key === mergeTarget)
      const targetValue = targetPerson?.email || targetPerson?.label || mergeTarget
      for (const key of selected) {
        if (key === mergeTarget) continue
        const p = people.find((pp) => pp.key === key)
        const sourceValue = p?.email || p?.label || key
        const res = await fetch(`${API_BASE}/v1/people/merge`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: sourceValue, target: targetValue })
        })
        if (!res.ok) {
          const data = await res.json().catch(() => null)
          throw new Error(data?.error || `統合に失敗しました (${res.status})`)
        }
      }
      setSelected(new Set())
      setShowMergeDialog(false)
      setMergeTarget('')
      await fetchPeople()
    } catch (e) {
      setError(e instanceof Error ? e.message : '統合に失敗しました')
    } finally {
      setMerging(false)
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return people
    return people.filter((p) =>
      p.label.toLowerCase().includes(q) || (p.email || '').includes(q)
    )
  }, [people, query])

  return (
    <Layout title="人物" showSidebar={true}>
      <div className="flex flex-col gap-4 container-md w-full">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <input
            type="text"
            className="form-input"
            style={{ maxWidth: 320 }}
            placeholder="名前・メールで検索..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="flex items-center gap-3">
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd((v) => !v)}>
              ＋ 人物を追加
            </button>
            {selected.size >= 2 && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { setMergeTarget([...selected][0]); setShowMergeDialog(true) }}
              >
                選択した{selected.size}人を統合
              </button>
            )}
            {selected.size > 0 && (
              <button className="btn btn-outline btn-sm" onClick={() => setSelected(new Set())}>
                選択解除
              </button>
            )}
            <span className="text-sm text-tertiary">{filtered.length}人</span>
          </div>
        </div>

        {/* 人物の手動追加 */}
        {showAdd && (
          <div className="card" style={{ padding: 16 }}>
            <div className="text-sm font-semibold mb-2">人物を追加</div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                className="form-input text-sm"
                style={{ flex: '1 1 160px' }}
                placeholder="名前"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
              />
              <input
                type="email"
                className="form-input text-sm"
                style={{ flex: '1 1 200px' }}
                placeholder="メールアドレス（任意）"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
              />
              <button
                className="btn btn-primary btn-sm"
                disabled={adding || (!addName.trim() && !addEmail.trim())}
                onClick={handleAddPerson}
              >
                {adding && <span className="spinner sm" />}
                追加
              </button>
            </div>
            <p className="text-xs text-tertiary" style={{ marginTop: 8 }}>
              追加した人物はリストに表示され、議事録の参加者と同じ名前・メールなら自動で履歴が紐づきます。
            </p>
          </div>
        )}

        {/* 統合ダイアログ: 正とする人物を選ぶ */}
        {showMergeDialog && (
          <div className="card" style={{ border: '1px solid var(--color-accent)', padding: 16 }}>
            <div className="text-sm font-semibold mb-2">どの表記に統合しますか？</div>
            <p className="text-xs text-secondary mb-3">
              選ばれた表記が「正」となり、他の表記のタイムライン・会議履歴がこの人物にまとめて表示されるようになります。
            </p>
            <div className="flex flex-col gap-1 mb-3">
              {[...selected].map((key) => {
                const p = people.find((pp) => pp.key === key)
                if (!p) return null
                return (
                  <label key={key} className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer', padding: '4px 0' }}>
                    <input
                      type="radio"
                      name="merge-target"
                      checked={mergeTarget === key}
                      onChange={() => setMergeTarget(key)}
                    />
                    <span className="font-medium">{p.label}</span>
                    {p.email && <span className="text-xs text-tertiary">{p.email}</span>}
                    {p.isMember && <span className="badge badge-neutral">メンバー</span>}
                  </label>
                )
              })}
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-primary btn-sm" disabled={merging || !mergeTarget} onClick={handleBulkMerge}>
                {merging && <span className="spinner sm" />}
                統合する
              </button>
              <button className="btn btn-outline btn-sm" disabled={merging} onClick={() => setShowMergeDialog(false)}>
                キャンセル
              </button>
            </div>
          </div>
        )}

        {error && <div className="alert danger">{error}</div>}

        {loading ? (
          <div className="flex items-center gap-2 py-8 justify-center">
            <span className="spinner" />
            <span className="text-secondary text-sm">読み込み中...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <h3 className="empty-state-title">人物がいません</h3>
              <p className="empty-state-desc">議事録に参加者が記録されるとここに表示されます。</p>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            {filtered.map((p, i) => (
              <Link
                key={p.key}
                to={`/people/${encodeURIComponent(p.email || p.label)}`}
                className="flex items-center gap-3 px-4 py-3"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  ...(i > 0 ? { borderTop: '1px solid var(--color-border-light)' } : {}),
                  ...(selected.has(p.key) ? { background: 'var(--color-accent-light, #eef1f7)' } : {})
                }}
              >
                <span
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(p.key) }}
                  style={{ display: 'flex', alignItems: 'center', flexShrink: 0, padding: 4, margin: -4 }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.key)}
                    readOnly
                    style={{ width: 16, height: 16, cursor: 'pointer', pointerEvents: 'none' }}
                  />
                </span>
                {p.photoURL ? (
                  <img src={p.photoURL} alt={p.label} className="avatar" style={{ objectFit: 'cover', border: 'none', flexShrink: 0 }} />
                ) : (
                  <span className="avatar" style={{ backgroundColor: avatarColor(p.email || p.label), color: '#fff', border: 'none', flexShrink: 0 }}>
                    {avatarInitial(p.label)}
                  </span>
                )}
                <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
                  <span className="text-sm font-medium truncate">
                    {p.label}
                    {p.isMember && <span className="badge badge-neutral" style={{ marginLeft: 8 }}>メンバー</span>}
                  </span>
                  {p.email && <span className="text-xs text-tertiary truncate">{p.email}</span>}
                </div>
                <div className="flex flex-col items-end" style={{ flexShrink: 0 }}>
                  <span className="text-xs text-secondary">{p.meetingCount}件の会議</span>
                  <span className="text-xs text-tertiary">{formatDate(p.lastSeen)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
